import React, { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, Instagram, Linkedin, Facebook, Lock, ChevronDown, Navigation } from "lucide-react";
import { footer, brand } from "../mock/content";
import { track } from "../lib/analytics";
import { getActiveLang } from "../lib/activeLang";
import { localizePath, isNonLocalized } from "../lib/locale";
import { t } from "../lib/ui";
import { openCookiePrefs } from "../lib/consent";
import { vestaVisible } from "../lib/vestaLaunch";
const COOKIE_LBL = { it: "Preferenze cookie", en: "Cookie preferences", de: "Cookie-Einstellungen", fr: "Préférences cookies" };

const QUICK = {
  it: ["Chiama", "Email", "Come arrivare"], en: ["Call", "Email", "Directions"],
  de: ["Anrufen", "E-Mail", "Anfahrt"], fr: ["Appeler", "E-mail", "Itinéraire"],
};
const MAPS = "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent("TORRI Solare, Via Cremona 123, 25025 Manerbio BS");
const SOCIALS = [{ Icon: Instagram, href: "https://www.instagram.com/torri.solar/", label: "instagram" }, { Icon: Linkedin, href: "https://www.linkedin.com/company/torrisolare/", label: "linkedin" }, { Icon: Facebook, href: "https://www.facebook.com/share/195fw6Cp18/?mibextid=wwXIfr", label: "facebook" }];

function Socials({ prefix = "footer-social" }) {
  return (
    <div className="flex gap-3">
      {SOCIALS.map(({ Icon, href, label }) => (
        <a key={label} data-testid={`${prefix}-${label}`} href={href} target="_blank" rel="noreferrer" aria-label={label} className="w-11 h-11 md:w-9 md:h-9 flex items-center justify-center border border-white/15 hover:bg-[#F9E212] hover:text-black hover:border-transparent transition-colors">
          <Icon size={16} />
        </a>
      ))}
    </div>
  );
}

function QuickContacts() {
  const L = QUICK[getActiveLang()] || QUICK.it;
  const items = [
    { Icon: Phone, label: L[0], href: footer.contact.phoneHref, id: "call" },
    { Icon: Mail, label: L[1], href: `mailto:${footer.contact.email}`, id: "email" },
    { Icon: Navigation, label: L[2], href: MAPS, id: "directions", ext: true },
  ];
  return (
    <div className="md:hidden" data-testid="footer-quick-contacts">
      <div className="grid grid-cols-3 gap-2">
        {items.map(({ Icon, label, href, id, ext }, i) => (
          <a key={id} href={href} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined} onClick={() => track("footer_quick_contact", { type: id })}
            className={`h-16 px-1 flex flex-col items-center justify-center gap-1.5 text-center leading-tight text-xs font-bold uppercase tracking-[0.04em] ${i === 0 ? "text-black" : "text-white border border-white/20"}`}
            style={{ fontFamily: "Space Grotesk", background: i === 0 ? brand.yellow : undefined }} data-testid={`footer-quick-${id}`}>
            <Icon size={18} /> {label}
          </a>
        ))}
      </div>
      <p className="mt-4 text-sm text-white/70 flex items-center gap-2" style={{ fontFamily: "Inter" }}><MapPin size={15} style={{ color: brand.yellow }} /> {footer.contact.address}</p>
      <div className="mt-4"><Socials prefix="footer-quick-social" /></div>
    </div>
  );
}

function LinkColumn({ col, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lg:col-span-2 border-b border-white/10 md:border-0" data-testid={testid}>
      <h4 className="text-white/60 text-[12px] uppercase tracking-[0.2em] md:mb-5" style={{ fontFamily: "Space Grotesk" }}>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="md:pointer-events-none w-full flex items-center justify-between py-4 md:py-0 text-left uppercase tracking-[0.2em]" data-testid={`footer-toggle-${col.title}`}>
          {col.title}
          <ChevronDown size={16} className={`md:hidden transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
      </h4>
      <ul className={`${open ? "block" : "hidden"} md:block pb-4 md:pb-0 space-y-1 md:space-y-3`}>
        {col.links.filter(vestaVisible).map((l) => (
          <li key={l.label}>
            <a href={locHref(l.href)} target={l.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="inline-block py-1.5 md:py-0 text-white/70 hover:text-white text-sm transition-colors" style={{ fontFamily: "Inter" }}>
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

// Prefissa i link interni localizzabili con la lingua attiva.
const locHref = (href) => {
  if (!href || !href.startsWith("/") || isNonLocalized(href)) return href;
  return localizePath(href, getActiveLang());
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSent(true);
    const value = email;
    setEmail("");
    track("newsletter_signup", { source: "Footer" });
    setTimeout(() => setSent(false), 3500);
    try {
      await fetch(`${API}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, source: "Footer", hp }),
      });
    } catch {
      /* fail-safe */
    }
  };

  return (
    <footer id="footer" className="bg-black text-white border-t border-white/10">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="font-black tracking-tight leading-[0.95]" style={{ fontFamily: "Archivo", fontSize: "clamp(2rem,4vw,3.2rem)" }}>
              {t("nl_title_pre")}<span style={{ color: brand.yellow }}>{t("nl_title_accent")}</span>
            </h3>
            <p className="mt-3 text-white/55" style={{ fontFamily: "Inter" }}>
              {t("nl_sub")}
            </p>
          </div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              name="company_url"
              tabIndex={-1}
              autoComplete="off"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("nl_placeholder")}
              className="flex-1 bg-transparent border border-white/25 px-5 py-4 text-white placeholder-white/40 focus:outline-none focus:border-[#F9E212] transition-colors"
              style={{ fontFamily: "Inter" }}
            />
            <button type="submit" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform hover:scale-[1.03]" style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
              {sent ? t("nl_btn_sent") : t("nl_btn")} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 md:py-16 pb-28 md:pb-16">
        <QuickContacts />
        <div className="mt-6 md:mt-0 grid lg:grid-cols-12 gap-0 md:gap-10">
          <div className="lg:col-span-2 hidden md:block">
            <div className="mt-6" data-testid="footer-confindustria">
              <img loading="lazy" decoding="async"
                src="/media/Logo-Associato-confindustria-brescia-torri.png"
                alt="Associato a Confindustria Brescia"
                className="h-[74px] w-auto object-contain"
              />
            </div>
          </div>

          {footer.columns.map((col) => <LinkColumn key={col.title} col={col} />)}

          <div className="lg:col-span-2 hidden md:block">
            <h4 className="text-white/60 text-[12px] uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "Space Grotesk" }}>{t("contatti")}</h4>
            <ul className="space-y-4 text-sm" style={{ fontFamily: "Inter" }}>
              <li><a href={footer.contact.phoneHref} className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"><Phone size={16} style={{ color: brand.yellow }} /> {footer.contact.phone}</a></li>
              <li><a href={`mailto:${footer.contact.email}`} className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"><Mail size={16} style={{ color: brand.yellow }} /> {footer.contact.email}</a></li>
              <li className="flex items-center gap-3 text-white/70"><MapPin size={16} style={{ color: brand.yellow }} /> {footer.contact.address}</li>
            </ul>
            <div className="mt-6"><Socials /></div>
          </div>

          {footer.extraColumn && <LinkColumn col={footer.extraColumn} testid="footer-extra-column" />}

          <div className="md:hidden mt-8" data-testid="footer-confindustria-mobile">
            <img loading="lazy" decoding="async"
              src="/media/Logo-Associato-confindustria-brescia-torri.png"
              alt="Associato a Confindustria Brescia" className="h-[56px] w-auto object-contain" />
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-white/60 text-xs text-center md:text-left" style={{ fontFamily: "Inter" }}>© {new Date().getFullYear()} Fier S.p.A. · TORRI — Beyond Ordinary Solar</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center md:justify-end gap-x-6 gap-y-4">
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                {footer.legal.map((l) => (
                  <a key={l.label} href={locHref(l.href)} className="text-white/60 hover:text-white text-xs transition-colors" style={{ fontFamily: "Inter" }}>{l.label}</a>
                ))}
                <button type="button" onClick={openCookiePrefs} className="text-white/60 hover:text-white text-xs transition-colors" style={{ fontFamily: "Inter" }} data-testid="footer-cookie-prefs">{COOKIE_LBL[getActiveLang()] || COOKIE_LBL.it}</button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a href="/area-clienti" data-testid="footer-clienti-link" className="inline-flex items-center gap-2 px-4 py-2 text-black text-xs uppercase tracking-[0.12em] font-semibold transition-transform hover:scale-[1.04]" style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
                  <Lock size={13} /> {t("area_clienti")}
                </a>
                <a href="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-2 px-4 py-2 border text-xs uppercase tracking-[0.12em] font-semibold transition-colors hover:bg-[#F9E212] hover:text-black" style={{ borderColor: brand.yellow, color: brand.yellow, fontFamily: "Space Grotesk" }}>
                  <Lock size={13} /> {t("area_riservata")}
                </a>
              </div>
            </div>
          </div>
          {/* Dati legali societari */}
          <div className="mt-6 space-y-1 text-white/60 text-xs leading-relaxed" style={{ fontFamily: "Inter" }} data-testid="footer-legal-info">
            <p>Fier S.p.A. a socio unico — Via Cremona, 123 — 25025 Manerbio (BS) — P.I./C.F. 03720770985 — Capitale sociale 500.000,00 € i.v.</p>
            <p>Registro A.E.E. n° IT16120000009638 — Registro Produttori Pile e Accumulatori n° IT23100P00009100</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
